import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Divider, Paper, Stack, TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import { apiFetch } from '../utils/apiFetch.js';

const days = [
  ['monday', 'Monday'], ['tuesday', 'Tuesday'], ['wednesday', 'Wednesday'],
  ['thursday', 'Thursday'], ['friday', 'Friday'], ['saturday', 'Saturday'], ['sunday', 'Sunday'],
];

const defaultSchedule = () => Object.fromEntries(days.map(([day]) => [
  day,
  [{ start: '08:00', end: '18:00' }],
]));

export default function MoverAvailabilityEditor({ token }) {
  const [availability, setAvailability] = useState({ weekly: defaultSchedule(), exceptions: [], timezone: 'America/Toronto', bufferMinutes: 60 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    apiFetch('/api/calendar/availability', { headers })
      .then(async response => {
        if (!response.ok) throw new Error('Unable to load availability');
        return response.json();
      })
      .then(data => setAvailability({
        timezone: data.timezone || 'America/Toronto',
        bufferMinutes: data.bufferMinutes ?? 60,
        weekly: { ...defaultSchedule(), ...(data.weekly || {}) },
        exceptions: Array.isArray(data.exceptions) ? data.exceptions : [],
      }))
      .catch(() => setError('Unable to load your availability.'))
      .finally(() => setLoading(false));
  }, [token]);

  const updateDay = (day, windows) => setAvailability(current => ({
    ...current,
    weekly: { ...current.weekly, [day]: windows },
  }));

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await apiFetch('/api/calendar/availability', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify(availability),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to save availability');
      setAvailability(data);
      setMessage('Availability saved. Customers will only see you during these hours.');
    } catch (err) {
      setError(err.message || 'Unable to save availability.');
    } finally {
      setSaving(false);
    }
  };

  const addException = () => setAvailability(current => ({
    ...current,
    exceptions: [...current.exceptions, { date: new Date().toISOString().slice(0, 10), available: false, reason: '' }],
  }));

  if (loading) return <Paper sx={{ p: 2, mb: 2 }}><CircularProgress size={22} /></Paper>;

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 2.5, border: '1px solid #D9E2EC', borderRadius: 2.5, bgcolor: '#fff' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#102A43' }}>Availability</Typography>
          <Typography variant="body2" color="text.secondary">
            Set the hours when customers can request your moving services.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save availability'}
        </Button>
      </Box>

      {message && <Alert severity="success" sx={{ mt: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {days.map(([day, label]) => (
          <Box key={day} sx={{ p: 1.5, borderRadius: 2, bgcolor: day === 'saturday' || day === 'sunday' ? '#F8FAFC' : '#F4F8FA', border: '1px solid #E6EDF3' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: (availability.weekly[day] || []).length ? 1 : 0 }}>
              <Typography fontWeight={750}>{label}</Typography>
              <Button size="small" startIcon={<Add />} onClick={() => updateDay(day, [...(availability.weekly[day] || []), { start: '08:00', end: '17:00' }])}>
                Add hours
              </Button>
            </Box>
            <Stack spacing={1}>
              {(availability.weekly[day] || []).map((window, index) => (
                <Box key={`${day}-${index}`} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 38px', gap: 1, alignItems: 'center' }}>
                  <TextField
                    size="small" type="time" label="Start" value={window.start}
                    onChange={event => updateDay(day, availability.weekly[day].map((item, itemIndex) => itemIndex === index ? { ...item, start: event.target.value } : item))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small" type="time" label="End" value={window.end}
                    onChange={event => updateDay(day, availability.weekly[day].map((item, itemIndex) => itemIndex === index ? { ...item, end: event.target.value } : item))}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button aria-label={`Remove ${label} time window`} size="small" color="error" onClick={() => updateDay(day, availability.weekly[day].filter((_, itemIndex) => itemIndex !== index))}>
                    <Delete fontSize="small" />
                  </Button>
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>

      <Divider sx={{ my: 2.5 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Unavailable dates</Typography>
          <Typography variant="body2" color="text.secondary">Block holidays, vacation, or other days off.</Typography>
        </Box>
        <Button size="small" startIcon={<Add />} onClick={addException}>Add unavailable date</Button>
      </Box>
      <Stack spacing={1} sx={{ mt: 1.5 }}>
        {availability.exceptions.map((exception, index) => (
          <Box key={`${exception.date}-${index}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px minmax(0, 1fr) 38px' }, gap: 1, alignItems: 'center' }}>
            <TextField size="small" type="date" label="Date" value={String(exception.date).slice(0, 10)} InputLabelProps={{ shrink: true }}
              onChange={event => setAvailability(current => ({ ...current, exceptions: current.exceptions.map((item, itemIndex) => itemIndex === index ? { ...item, date: event.target.value } : item) }))} />
            <TextField size="small" label="Reason (optional)" value={exception.reason || ''}
              onChange={event => setAvailability(current => ({ ...current, exceptions: current.exceptions.map((item, itemIndex) => itemIndex === index ? { ...item, reason: event.target.value } : item) }))} />
            <Button aria-label="Remove unavailable date" size="small" color="error" onClick={() => setAvailability(current => ({ ...current, exceptions: current.exceptions.filter((_, itemIndex) => itemIndex !== index) }))}>
              <Delete fontSize="small" />
            </Button>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}
